import axios from 'axios';
import { create } from 'zustand';
import { pasService } from './pasService';
import type {
  ApprovedQuote,
  Broker,
  Carrier,
  NewPolicyDraft,
  PasUser,
  Policy,
  PolicyFilters,
  PolicyNote,
  Toast,
  ToastVariant,
  UnderwritingDecision,
  WorkflowSettings,
} from './types';
import { makeId } from './utils';

const EMPTY_FILTERS: PolicyFilters = {
  search: '',
  statuses: [],
  brokerId: null,
  carrierId: null,
  product: null,
};

/**
 * When to re-fetch a policy after a document-generating action, in milliseconds
 * since the action, so async-rendered document URLs hydrate. Checks stop early
 * once every document has a download URL.
 */
export const DOC_HYDRATION_SCHEDULE_MS = [3000, 6000, 12000, 20000, 30000, 45000];

/** Pending hydration timers, keyed by policy id, so a newer action replaces an older schedule. */
const hydrationTimers = new Map<string, ReturnType<typeof setTimeout>>();

/** Pull a human-readable message out of an API error (DRF `detail`, field errors, or a string body). */
function extractMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;
      if (typeof record.detail === 'string') return record.detail;
      if (typeof record.message === 'string') return record.message;
      const first = Object.values(record)[0];
      if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
      if (typeof first === 'string') return first;
    }
  }
  return fallback;
}

interface PasState {
  policies: Policy[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
  filters: PolicyFilters;
  selectedIds: string[];
  toasts: Toast[];

  // Reference data (load-once)
  brokers: Broker[];
  carriers: Carrier[];
  products: string[];
  users: PasUser[];
  workflowSettings: WorkflowSettings | null;
  approvedQuotes: ApprovedQuote[];
  referenceLoaded: boolean;
  referenceLoading: boolean;

  fetchPolicies: (force?: boolean) => Promise<void>;
  fetchReferenceData: (force?: boolean) => Promise<void>;
  refreshPolicy: (id: string) => Promise<void>;
  getPolicy: (id: string) => Policy | undefined;

  setFilters: (patch: Partial<PolicyFilters>) => void;
  clearFilters: () => void;

  toggleSelect: (id: string) => void;
  setSelection: (ids: string[]) => void;
  clearSelection: () => void;

  pushToast: (variant: ToastVariant, title: string, message?: string) => void;
  dismissToast: (id: string) => void;

  createFromQuote: (draft: NewPolicyDraft) => Promise<Policy>;
  submitToUnderwriting: (id: string) => Promise<void>;
  bulkSubmitToUnderwriting: (ids: string[]) => Promise<void>;
  setChecklistItem: (id: string, itemId: string, done: boolean) => Promise<void>;
  recordDecision: (id: string, decision: UnderwritingDecision['decision'], rationale: string) => Promise<void>;
  generateBinder: (id: string) => Promise<void>;
  issuePolicy: (id: string) => Promise<void>;
  activatePolicy: (id: string) => Promise<void>;
  cancelPolicy: (id: string, reason: string, note: string, cancellationEndDate: string) => Promise<void>;
  renewPolicy: (id: string, opts: { effectiveDate: string; annualPremium: number; note: string }) => Promise<Policy>;
  addNote: (id: string, body: string, category: PolicyNote['category']) => Promise<void>;
  setNotePinned: (id: string, noteId: string, pinned: boolean) => Promise<void>;
  addDocumentVersion: (id: string, docId: string, note: string) => Promise<void>;
}

export const usePasStore = create<PasState>((set, get) => {
  const upsert = (policy: Policy) =>
    set((state) => {
      const exists = state.policies.some((p) => p.id === policy.id);
      return {
        policies: exists
          ? state.policies.map((p) => (p.id === policy.id ? policy : p))
          : [policy, ...state.policies],
      };
    });

  const toast = (variant: ToastVariant, title: string, message?: string) =>
    set((state) => ({ toasts: [...state.toasts, { id: makeId('toast'), variant, title, message }] }));

  /** Map backend errors to a toast. 409 → workflow conflict, 400 → validation, else generic. */
  const reportError = (err: unknown, fallbackTitle: string) => {
    let variant: ToastVariant = 'error';
    let title = fallbackTitle;
    if (axios.isAxiosError(err)) {
      const status = err.response?.status;
      if (status === 409) {
        variant = 'warning';
        title = 'Workflow conflict';
      } else if (status === 400) {
        title = 'Validation error';
      }
    }
    toast(variant, title, extractMessage(err, 'Please try again.'));
  };

  /**
   * Re-fetch a policy on DOC_HYDRATION_SCHEDULE_MS after a document-generating
   * action until all of its documents have presigned URLs. Rendering can outlast
   * any single delay (cold worker, several PDFs), so one re-fetch isn't enough.
   */
  const scheduleDocHydration = (id: string) => {
    const pending = hydrationTimers.get(id);
    if (pending) clearTimeout(pending);

    const check = (step: number) => {
      const elapsed = step === 0 ? 0 : DOC_HYDRATION_SCHEDULE_MS[step - 1];
      const timer = setTimeout(async () => {
        await get().refreshPolicy(id);
        // A newer action may have rescheduled this policy while we were fetching.
        if (hydrationTimers.get(id) !== timer) return;
        const policy = get().getPolicy(id);
        const hydrated = !!policy && policy.documents.every((doc) => !!doc.downloadUrl);
        if (hydrated || step + 1 >= DOC_HYDRATION_SCHEDULE_MS.length) {
          hydrationTimers.delete(id);
          return;
        }
        check(step + 1);
      }, DOC_HYDRATION_SCHEDULE_MS[step] - elapsed);
      hydrationTimers.set(id, timer);
    };

    check(0);
  };

  return {
    policies: [],
    loading: false,
    loaded: false,
    error: null,
    filters: EMPTY_FILTERS,
    selectedIds: [],
    toasts: [],

    brokers: [],
    carriers: [],
    products: [],
    users: [],
    workflowSettings: null,
    approvedQuotes: [],
    referenceLoaded: false,
    referenceLoading: false,

    fetchPolicies: async (force = false) => {
      if (get().loaded && !force) return;
      set({ loading: true, error: null });
      try {
        const policies = await pasService.list();
        set({ policies, loading: false, loaded: true });
      } catch {
        set({ error: 'Failed to load policies', loading: false });
      }
    },

    fetchReferenceData: async (force = false) => {
      if ((get().referenceLoaded || get().referenceLoading) && !force) return;
      set({ referenceLoading: true });
      try {
        const [brokers, carriers, products, users, workflowSettings, approvedQuotes] = await Promise.all([
          pasService.listBrokers(),
          pasService.listCarriers(),
          pasService.listProducts(),
          pasService.listUsers(),
          pasService.getWorkflowSettings(),
          pasService.listApprovedQuotes(),
        ]);
        set({
          brokers,
          carriers,
          products,
          users,
          workflowSettings,
          approvedQuotes,
          referenceLoaded: true,
          referenceLoading: false,
        });
      } catch (err) {
        set({ referenceLoading: false });
        reportError(err, 'Could not load reference data');
      }
    },

    refreshPolicy: async (id) => {
      try {
        const policy = await pasService.get(id);
        if (policy) upsert(policy);
      } catch {
        // Background refresh; failures are non-fatal and already surfaced elsewhere.
      }
    },

    getPolicy: (id) => get().policies.find((p) => p.id === id),

    setFilters: (patch) => set((state) => ({ filters: { ...state.filters, ...patch } })),
    clearFilters: () => set({ filters: EMPTY_FILTERS }),

    toggleSelect: (id) =>
      set((state) => ({
        selectedIds: state.selectedIds.includes(id)
          ? state.selectedIds.filter((x) => x !== id)
          : [...state.selectedIds, id],
      })),
    setSelection: (ids) => set({ selectedIds: ids }),
    clearSelection: () => set({ selectedIds: [] }),

    pushToast: (variant, title, message) => toast(variant, title, message),
    dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),

    createFromQuote: async (draft) => {
      try {
        const policy = await pasService.createFromQuote(draft);
        upsert(policy);
        toast('success', 'Draft policy created', `${policy.policyNumber} is ready for underwriting.`);
        return policy;
      } catch (err) {
        reportError(err, 'Could not create policy');
        throw err;
      }
    },

    submitToUnderwriting: async (id) => {
      try {
        const policy = await pasService.submitToUnderwriting(id);
        upsert(policy);
        toast('info', 'Submitted to underwriting', `${policy.policyNumber} assigned to ${policy.underwriting.assignedTo}.`);
      } catch (err) {
        reportError(err, 'Could not submit to underwriting');
        throw err;
      }
    },

    bulkSubmitToUnderwriting: async (ids) => {
      const eligible = get().policies.filter((p) => ids.includes(p.id) && p.status === 'draft');
      let succeeded = 0;
      for (const policy of eligible) {
        try {
          upsert(await pasService.submitToUnderwriting(policy.id));
          succeeded += 1;
        } catch (err) {
          reportError(err, `Could not submit ${policy.policyNumber}`);
        }
      }
      set({ selectedIds: [] });
      if (succeeded > 0) {
        toast('info', 'Submitted to underwriting', `${succeeded} ${succeeded === 1 ? 'policy' : 'policies'} routed to the queue.`);
      }
    },

    setChecklistItem: async (id, itemId, done) => {
      try {
        upsert(await pasService.setChecklistItem(id, itemId, done));
      } catch (err) {
        reportError(err, 'Could not update checklist');
        throw err;
      }
    },

    recordDecision: async (id, decision, rationale) => {
      try {
        const policy = await pasService.recordDecision(id, decision, rationale);
        upsert(policy);
        const label =
          decision === 'approved'
            ? 'Underwriting approved'
            : decision === 'declined'
              ? 'Underwriting declined'
              : decision === 'referred'
                ? 'Referred to senior underwriter'
                : 'Information requested';
        toast(decision === 'declined' ? 'warning' : 'success', label, policy.policyNumber);
      } catch (err) {
        reportError(err, 'Could not record decision');
        throw err;
      }
    },

    generateBinder: async (id) => {
      try {
        const policy = await pasService.generateBinder(id);
        upsert(policy);
        toast('success', 'Binder generated', `Binder issued for ${policy.policyNumber}.`);
        scheduleDocHydration(id);
      } catch (err) {
        reportError(err, 'Could not generate binder');
        throw err;
      }
    },

    issuePolicy: async (id) => {
      try {
        const policy = await pasService.issuePolicy(id);
        upsert(policy);
        toast('success', 'Policy issued', `${policy.policyNumber} documents are ready.`);
        scheduleDocHydration(id);
      } catch (err) {
        reportError(err, 'Could not issue policy');
        throw err;
      }
    },

    activatePolicy: async (id) => {
      try {
        const policy = await pasService.activatePolicy(id);
        upsert(policy);
        toast('success', 'Policy activated', `${policy.policyNumber} is now in force.`);
      } catch (err) {
        reportError(err, 'Could not activate policy');
        throw err;
      }
    },

    cancelPolicy: async (id, reason, note, cancellationEndDate) => {
      try {
        const policy = await pasService.cancelPolicy(id, reason, note, cancellationEndDate);
        upsert(policy);
        toast('warning', 'Policy cancelled', `${policy.policyNumber} — ${reason}.`);
      } catch (err) {
        reportError(err, 'Could not cancel policy');
        throw err;
      }
    },

    renewPolicy: async (id, opts) => {
      try {
        const { original, renewal } = await pasService.renewPolicy(id, opts);
        upsert(original);
        upsert(renewal);
        toast('success', 'Renewal created', `${renewal.policyNumber} created from ${original.policyNumber}.`);
        return renewal;
      } catch (err) {
        reportError(err, 'Could not renew policy');
        throw err;
      }
    },

    addNote: async (id, body, category) => {
      try {
        upsert(await pasService.addNote(id, body, category));
      } catch (err) {
        reportError(err, 'Could not add note');
        throw err;
      }
    },

    setNotePinned: async (id, noteId, pinned) => {
      try {
        upsert(await pasService.setNotePinned(id, noteId, pinned));
      } catch (err) {
        reportError(err, 'Could not update note');
        throw err;
      }
    },

    addDocumentVersion: async (id, docId, note) => {
      try {
        const policy = await pasService.addDocumentVersion(id, docId, note);
        upsert(policy);
        toast('success', 'Document regenerated', 'A new version has been generated.');
        scheduleDocHydration(id);
      } catch (err) {
        reportError(err, 'Could not regenerate document');
        throw err;
      }
    },
  };
});
